/* eslint-disable camelcase */
import React, { useEffect } from 'react';
import classNames from 'classnames';
import Select from '../../form/Select';
import DatePickerInstance from './DatePickerInstance';

const BuildPLUploadForm = ({
	formFields,
	data,
	updateData,
	isLicenseNumberRequired,
	setIsLicenseNumberRequired,
	getValidationErrorMessage,
	setValidationErrors
}) => {
	const filterStatesBasedOnLicense = (license, states) => {
		if (license === 'CC') {
			return [{ label: 'Arizona', value: 'AZ' }];
		}
		if (license === 'LNA') {
			return [
				{ label: 'Arizona', value: 'AZ' },
				{ label: 'New Hampshire', value: 'NH' }
			];
		}
		if (license === 'STNA') {
			return [{ label: 'Ohio', value: 'OH' }];
		}
		if (license === 'GNA') {
			return [{ label: 'Maryland', value: 'MD' }];
		}
		if (license === 'CNA') {
			return states.filter((el) => el.value !== 'OH');
		}

		return states;
	};

	const filterLicensesBasedOnState = (licenses, state) => {
		if (state === 'AZ') {
			return licenses.filter((item) => {
				return !['GNA', 'STNA'].includes(item.value);
			});
		}
		if (state === 'OH') {
			return licenses.filter((item) => {
				return !['GNA', 'CNA', 'CC', 'LNA'].includes(item.value);
			});
		}
		if (state === 'MD') {
			return licenses.filter((item) => {
				return !['STNA', 'CC', 'LNA'].includes(item.value);
			});
		}

		if (state && state !== 'NH') {
			return licenses.filter((item) => {
				return !['LNA'].includes(item.value);
			});
		}
		return licenses;
	};

	const getDropdownOptions = (options, fieldName) => {
		if (fieldName === 'state_code') {
			const stateOptions = options.map((option) => {
				return { label: option.name, value: option.code };
			});
			return filterStatesBasedOnLicense(data?.certification_type, stateOptions);
		}

		if (fieldName === 'certification_type') {
			const licenseOptions = options.map((option) => {
				return { label: option.name, value: option.name };
			});
			return filterLicensesBasedOnState(licenseOptions, data?.state_code);
		}

		return options.map((option) => {
			return { label: option.name, value: option.code };
		});
	};

	const isLicenseNumberOptional = (type, stateIssued) => {
		switch (type) {
			case 'CNA': {
				return stateIssued === 'IL';
			}
			case 'BT': {
				return !['LA', 'MS', 'NV', 'OR', 'WA'].includes(stateIssued);
			}
			case 'Paraed': {
				return !['CA', 'IL', 'NJ', 'NY', 'OR'].includes(stateIssued);
			}
			case 'Psych': {
				return ![
					'AK',
					'CO',
					'CT',
					'DE',
					'FL',
					'GA',
					'HI',
					'ID',
					'IL',
					'IN',
					'IA',
					'KS',
					'ME',
					'MD',
					'MO',
					'NE',
					'NH',
					'NJ',
					'NY',
					'PA',
					'TN',
					'TX',
					'UT',
					'WI'
				].includes(stateIssued);
			}
			case 'MLT': {
				return !['FL', 'LA', 'NV', 'NY', 'RI', 'TN'].includes(stateIssued);
			}
			case 'Phleb': {
				return !['CA', 'FL', 'LA', 'OH', 'WA'].includes(stateIssued);
			}
			case 'UAP':
			case 'Admin Asst':
			case 'MA':
			case 'Instr Tech':
			case 'Staff Serv Sched':
			case 'PSR':
			case 'Surg Tech':
			case 'SPT':
			case 'DHA':
			case 'Endo Tech':
			case 'Med Recep': {
				return true;
			}
			case 'Rad Tech': {
				return ![
					'AZ',
					'AR',
					'CA',
					'DE',
					'FL',
					'HI',
					'KY',
					'LA',
					'ME',
					'MD',
					'MA',
					'MT',
					'NE',
					'NV',
					'NH',
					'NM',
					'NY',
					'ND',
					'OH',
					'OR',
					'RI',
					'SC',
					'TX',
					'UT',
					'VT',
					'WA',
					'WV',
					'WI',
					'WY'
				].includes(stateIssued);
			}
			case 'Spec Ed Train': {
				return !['OR', 'TX'].includes(stateIssued);
			}
			case 'Sign Lang Interp': {
				return ![
					/* eslint-disable-next-line indent */
					'AR',
					'CA',
					'KY',
					'MN',
					'MS',
					'NM',
					'NC',
					'OK',
					'OR',
					'PA',
					'RI',
					'SC',
					'TX',
					'UT',
					'VA',
					'WV'
				].includes(stateIssued);
			}
			case 'Ultra Tech': {
				return !['NH', 'NM', 'ND', 'OR'].includes(stateIssued);
			}
			case 'LNHA': {
				return ![
					'AK',
					'CO',
					'CT',
					'DE',
					'FL',
					'GA',
					'HI',
					'ID',
					'IL',
					'IN',
					'IA',
					'LA',
					'MO',
					'NE',
					'NH',
					'NJ',
					'NY',
					'PA',
					'TN',
					'UT',
					'WI'
				].includes(stateIssued);
			}
			case 'QMA': {
				return ![
					'AL',
					'AR',
					'CO',
					'CT',
					'DE',
					'FL',
					'GA',
					'IL',
					'IN',
					'IA',
					'LA',
					'MO',
					'MT',
					'NE',
					'NJ',
					'SC',
					'TN',
					'TX',
					'WI'
				].includes(stateIssued);
			}
			case 'DA': {
				return ![
					'AR',
					'CA',
					'DC',
					'IA',
					'OK',
					'MA',
					'MI',
					'MN',
					'NE',
					'NV',
					'NM',
					'ND',
					'NY',
					'PA',
					'RI',
					'SD',
					'TN',
					'TX',
					'VT',
					'VA',
					'WA',
					'WY'
				].includes(stateIssued);
			}
			case 'DH': {
				return ['AL', 'AK', 'CA', 'NV', 'OR', 'PR', 'TX', 'WA', 'AS', 'GU', 'MP', 'VI'].includes(stateIssued);
			}
			case 'Echo Tech': {
				return !['NM', 'NH', 'ND', 'OR'].includes(stateIssued);
			}
			default: {
				return false;
			}
		}
	};

	useEffect(() => {
		const tempIsLicenseNumberOptional = isLicenseNumberOptional(data?.certification_type, data?.state_code);

		if (!data?.state_code && !data?.certification_type) {
			setIsLicenseNumberRequired(false);
			return;
		}

		if (tempIsLicenseNumberOptional) {
			setIsLicenseNumberRequired(false);
			return;
		}
		setIsLicenseNumberRequired(true);
	}, [data?.certification_type, data?.state_code, setIsLicenseNumberRequired]);

	const handleDropdownChange = (name, value) => {
		if (name === 'certification_type') {
			if (value === 'CC') {
				updateData('state_code', 'AZ');
			}
			if (value === 'STNA') {
				updateData('state_code', 'OH');
			}
			if (value === 'GNA') {
				updateData('state_code', 'MD');
			}
		}
		updateData(name, value);
	};

	const handleTextChange = (name, value) => {
		if (name === 'licence_number' && value.length > 2) {
			setValidationErrors((oldValidationErrors) => [
				...oldValidationErrors.filter((err) => err.field !== 'licence_number')
			]);
		}

		updateData(name, value);
	};

	return (
		<div className="upload-form">
			<div className="form-field credential-field">
				<Select
					name="credential"
					label="Credential"
					placeholder="Professional License"
					value="PROFESSIONAL_LICENSE"
					isDisabled
					className="form-select disabled-select"
				/>
				<div className="error-message" />
			</div>
			{formFields.map((field) => {
				const errorMessage = getValidationErrorMessage(field.name);

				if (field.control_type === 'dropdown') {
					return (
						<div
							className={classNames(`form-field ${field.name}-field`, { 'validation-error': !!errorMessage })}
							key={`${field.name}`}
						>
							<Select
								handleChange={(value) => handleDropdownChange(field.name, value[field.name])}
								options={getDropdownOptions(field.values, field.name)}
								name={field.name}
								label={field.label}
								placeholder="Select..."
								value={data[field.name]}
								className="form-select"
								isSearchable
							/>
							<div className="error-message">{errorMessage}</div>
						</div>
					);
				}
				if (field.control_type === 'text') {
					return (
						<div
							className={classNames(`form-field ${field.name}-field`, { 'validation-error': !!errorMessage })}
							key={`${field.name}`}
						>
							<div className="input-label">
								<span className="label-name">{field.label}</span>
								<span className={classNames('label-optional', { 'label-optional-disabled': isLicenseNumberRequired })}>
									(Optional)
								</span>
							</div>
							<input
								className="input form-input"
								type="text"
								value={data[field.name] || ''}
								onChange={(e) => handleTextChange(field.name, e.target.value)}
							/>
							<div className="error-message">{errorMessage}</div>
						</div>
					);
				}
				if (field.control_type === 'date') {
					return (
						<div className={classNames('form-field date', { 'validation-error': !!errorMessage })} key={field.name}>
							<div className="input-label">
								<span className="label-name">{field.label}</span>
							</div>
							<DatePickerInstance
								selectedDate={data[field.name]}
								updateDate={(date) => updateData(field.name, date)}
								placeholder="MM/DD/YYYY"
							/>
							<div className="error-message">{errorMessage}</div>
						</div>
					);
				}
				return '';
			})}
		</div>
	);
};

export default BuildPLUploadForm;
